import { TodayIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { MenuItem } from '../../modules/app-sidebar/views';
import { createTemplateDoc } from '../../modules/cabinet';
import { DocsService } from '../../modules/doc';
import { WorkspaceService } from '../../modules/workspace';
import { WorkbenchService } from '../../modules/workbench';

const planKey = (workspaceId: string) => `orghub:plan-doc:${workspaceId}`;
const PLAN_TITLE = 'План работ';

/**
 * Sidebar shortcut to the workspace plan ("Инфокарта").
 *
 * The plan is an ordinary document built from the timeline template: the first
 * click creates it, later clicks reopen the same doc. The id is remembered per
 * workspace in localStorage and — as a cross-device fallback — looked up by
 * title, since localStorage does not sync but the doc list does. This keeps the
 * plan editable like any other page instead of hiding it behind a separate
 * read-only screen.
 */
export const PlanButton = () => {
  const docsService = useService(DocsService);
  const workspace = useService(WorkspaceService).workspace;
  const workbench = useService(WorkbenchService).workbench;
  // workbench pathname is workspace-relative: '/all', '/<docId>', '/my-tasks'
  const location = useLiveData(workbench.location$);
  const [opening, setOpening] = useState(false);

  const workspaceId = workspace.id;
  const currentDocId = location.pathname.split('/').filter(Boolean).at(-1);
  const storedId = localStorage.getItem(planKey(workspaceId));
  const active = !!storedId && currentDocId === storedId;

  const openPlan = useCallback(async () => {
    if (opening) return;
    setOpening(true);
    try {
      const docs = docsService.list.docs$.value;
      let docId =
        storedId && docs.some(doc => doc.id === storedId) ? storedId : null;
      if (!docId) {
        // localStorage is device-local; reuse the synced plan doc by title
        // before creating a duplicate on a second device
        docId = docs.find(doc => doc.title$.value === PLAN_TITLE)?.id ?? null;
      }
      if (!docId) {
        docId = await createTemplateDoc(docsService, 'timeline', PLAN_TITLE);
      }
      localStorage.setItem(planKey(workspaceId), docId);
      workbench.openDoc(docId);
    } finally {
      setOpening(false);
    }
  }, [docsService, opening, storedId, workbench, workspaceId]);

  return (
    <MenuItem
      data-testid="plan-button"
      active={active}
      onClick={() => {
        openPlan().catch(console.error);
      }}
      icon={<TodayIcon />}
    >
      {opening ? 'Открываю…' : 'Инфокарта'}
    </MenuItem>
  );
};
